import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useTest } from "@/context/TestContext";
import { useToast } from "@/hooks/use-toast";
import { loadTestState, type SavedTestState } from "@/utils/localStorage";
import { usePlan } from "@/lib/plan";
import { reportNoActionsLeft } from "@/lib/outOfActions";
import { MAX_TESTS_PER_USER, type Test } from "@shared/schema";

/** The in-progress draft saved in this browser, if it has any real content. */
export function loadDraft(): SavedTestState | null {
  const state = loadTestState();
  if (!state) return null;
  const hasContent = state.config || state.blueprint || state.questions.length > 0;
  return hasContent ? state : null;
}

/**
 * Shared "start a new test" rules. Respects the per-user test cap and always
 * starts from a blank draft (the in-memory TestContext survives navigation, so
 * reset unconditionally). Drafts live in a single browser slot, so starting a
 * new test would overwrite one in progress — when a draft exists, `createTest`
 * opens a confirmation instead; render `confirmDialog` wherever the hook is used.
 */
export function useCreateTest() {
  const [, setLocation] = useLocation();
  const { resetTest, loadSavedState } = useTest();
  const { toast } = useToast();
  const { isOutOfActions, limit, resetsAt } = usePlan();
  const { data: tests } = useQuery<Test[]>({ queryKey: ["/api/tests"] });
  const [confirmOpen, setConfirmOpen] = useState(false);

  const atTestLimit = (tests?.length ?? 0) >= MAX_TESTS_PER_USER;

  const showLimitToast = () =>
    toast({
      variant: "destructive",
      title: "Test limit reached",
      description: `You can have up to ${MAX_TESTS_PER_USER} tests at a time. Delete one to make room.`,
    });

  const startFresh = () => {
    resetTest();
    setLocation("/create/identify");
  };

  /** Load the saved draft and jump to the furthest step it reached. */
  const continueDraft = (draft: SavedTestState | null = loadDraft()) => {
    if (!draft) return;
    // Reviewing generated questions costs nothing; every earlier step leads to generation.
    if (isOutOfActions && draft.questions.length === 0) {
      reportNoActionsLeft(limit, resetsAt);
      return;
    }
    loadSavedState(draft);
    if (draft.questions.length > 0) {
      setLocation("/create/review-test");
    } else if (draft.blueprint) {
      setLocation("/create/blueprint");
    } else {
      setLocation("/create/identify");
    }
  };

  const createTest = () => {
    if (isOutOfActions) {
      reportNoActionsLeft(limit, resetsAt);
      return;
    }
    if (atTestLimit) {
      showLimitToast();
      return;
    }
    if (loadDraft()) {
      setConfirmOpen(true);
      return;
    }
    startFresh();
  };

  const confirmDialog = (
    <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>You have a test in progress</AlertDialogTitle>
          <AlertDialogDescription>
            Starting a new test will discard the one you're working on. This can't be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={() => continueDraft()}
            data-testid="button-confirm-continue-draft"
          >
            Continue it instead
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={startFresh}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            data-testid="button-confirm-discard-draft"
          >
            Discard and start new
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { createTest, continueDraft, atTestLimit, showLimitToast, confirmDialog };
}
